import {parseNumber} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {
  MonomerCapGroup,
  MonomerNaturalAnalogue,
  MonomerVisualSymbol,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import MonomerStructureAndWeightConfigurationPane from 'molbio/monomers/admin/MonomerStructureAndWeightConfigurationPane';
import {gqlToAPINaturalAnalogue} from 'molbio/monomers/MonomersUtil';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import StepNav, {StepItemStatus} from 'ui-library/StepNav';

import {
  NaturalAnalogueToNucleotideComponent,
  NucleotideComponent,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  DEFAULT_MODIFIED_BASE_COLOR,
  MonomerCapGroupWithWildcard,
  WILDCARD_CAP_GROUP_SYMBOL,
} from '../MonomerConstants';
import {
  MonomerValidationInput,
  MonomerValidationErrors,
  validateMonomerInput,
  APIMonomer,
  createMonomers,
} from '../MonomersDAO';
import MonomerConfigurationPageContainer from './MonomerConfigurationPageContainer';
import MonomerManualCreationBuildPane from './MonomerManualCreationBuildPane';
import MonomerManualCreationDefinePane from './MonomerManualCreationDefinePane';

export enum MonomerManualCreationStepKey {
  BUILD = 'BUILD',
  DEFINE = 'DEFINE',
}

export default function MonomerManualCreationPage(): JSX.Element {
  const [currentStepKey, setCurrentStepKey] = useState(MonomerManualCreationStepKey.BUILD);
  const [buildPhaseCompleted, setBuildPhaseCompleted] = useState(false);
  const [definePhaseCompleted, setDefinePhaseCompleted] = useState(false);
  const [smilesInput, setSmilesInput] = useState('');
  const [canonicalSmiles, setCanonicalSmiles] = useState<string | null>(null);
  const [customMolecularWeight, setCustomMolecularWeight] = useState<string | null>(null);
  const [calculatedMolecularWeight, setCalculatedMolecularWeight] = useState<number | null>(null);
  const [editingMolecularWeight, setEditingMolecularWeight] = useState(false);
  const [attachmentPointsInput, setAttachmentPointsInput] = useState<Array<MonomerCapGroup | null> | null>(
    null
  );
  const [calculatedAttachmentPoints, setCalculatedAttachmentPoints] =
    useState<Array<MonomerCapGroupWithWildcard> | null>([]);
  const [visualSymbol, setVisualSymbol] = useState<MonomerVisualSymbol>(MonomerVisualSymbol.DIAMOND_FILLED); // only for phosphate analog monomers
  const [naturalAnalogue, setNaturalAnalogue] = useState<MonomerNaturalAnalogue>(
    MonomerNaturalAnalogue.R_LOWER
  );
  const [visualColor, setVisualColor] = useState<string>(DEFAULT_MODIFIED_SUGAR_COLOR); // only for non-phosphate components
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [structureImage, setStructureImage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<MonomerValidationErrors>({
    smiles: null,
    symbol: null,
    attachmentPoints: null,
    molecularWeight: null,
    structureImage: null,
    naturalAnalogue: null,
    customMolecularWeight: null,
    color: null,
  });
  const [hasCustomWeightError, setHasCustomWeightError] = useState(false);

  const doValidateAndSetValues = useCallback(
    async (
      monomerValidationInput: MonomerValidationInput,
      syncSmilesInputWithCanonicalSmiles: boolean
    ): Promise<void> => {
      const outputs = await validateMonomerInput([monomerValidationInput]);
      assert(outputs.length === 1, 'Expected exactly one monomer validation output');
      const {properties, errors, isValid} = outputs[0];
      setCanonicalSmiles(properties.canonicalSmiles);
      setCalculatedAttachmentPoints(properties.attachmentPoints);
      setAttachmentPointsInput(
        properties.attachmentPoints?.map((attachmentPoint) =>
          attachmentPoint !== WILDCARD_CAP_GROUP_SYMBOL ? attachmentPoint : null
        ) ?? null
      );
      setCalculatedMolecularWeight(properties.molecularWeight);
      setStructureImage(properties.structureImage);

      setValidationErrors(errors);

      const anyBuildPhaseErrors = errors.smiles || errors.attachmentPoints;
      setBuildPhaseCompleted(!anyBuildPhaseErrors);
      setDefinePhaseCompleted(isValid);
      if (syncSmilesInputWithCanonicalSmiles && properties.canonicalSmiles != null) {
        setSmilesInput(properties.canonicalSmiles);
      }
    },
    []
  );

  useEffect(() => {
    doValidateAndSetValues({smiles: ''}, false);
  }, [doValidateAndSetValues]);

  const buildFullValidationInput = (
    overrides: Partial<MonomerValidationInput> = {}
  ): MonomerValidationInput => ({
    name,
    symbol,
    naturalAnalogue: gqlToAPINaturalAnalogue(naturalAnalogue),
    smiles: smilesInput,
    capGroups: attachmentPointsInput,
    customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
    ...overrides,
  });

  const onChangeSmilesInput = (newSmilesInput: string): void => {
    setSmilesInput(newSmilesInput);
    doValidateAndSetValues({smiles: newSmilesInput}, false);
  };

  const onChangeAttachmentPointsInput = (
    newAttachmentPointsInput: Array<MonomerCapGroup | null> | null
  ): void => {
    setAttachmentPointsInput(newAttachmentPointsInput);
    doValidateAndSetValues(buildFullValidationInput({capGroups: newAttachmentPointsInput}), true);
  };

  const onChangeName = (newName: string): void => {
    setName(newName);
    doValidateAndSetValues(buildFullValidationInput({name: newName}), false);
  };

  const onChangeSymbol = (newSymbol: string): void => {
    setSymbol(newSymbol);
    doValidateAndSetValues(buildFullValidationInput({symbol: newSymbol}), false);
  };

  const onChangeNaturalAnalogue = (newNaturalAnalogue: MonomerNaturalAnalogue): void => {
    setNaturalAnalogue(newNaturalAnalogue);
    const nucleotideComponent = NaturalAnalogueToNucleotideComponent[newNaturalAnalogue];
    setVisualColor(
      nucleotideComponent === NucleotideComponent.SUGAR
        ? DEFAULT_MODIFIED_SUGAR_COLOR
        : DEFAULT_MODIFIED_BASE_COLOR
    );
    doValidateAndSetValues(
      buildFullValidationInput({naturalAnalogue: gqlToAPINaturalAnalogue(newNaturalAnalogue)}),
      false
    );
  };

  const onChangeCustomMolecularWeight = (newCustomMolecularWeight: string | null): void => {
    setCustomMolecularWeight(newCustomMolecularWeight);
    doValidateAndSetValues(
      buildFullValidationInput({
        customMolecularWeight:
          newCustomMolecularWeight != null ? parseNumber(newCustomMolecularWeight) : null,
      }),
      false
    );
  };

  const statusByStep = {
    [MonomerManualCreationStepKey.BUILD]: buildPhaseCompleted
      ? StepItemStatus.DONE
      : StepItemStatus.UNVISITED,
    [MonomerManualCreationStepKey.DEFINE]: definePhaseCompleted
      ? StepItemStatus.DONE
      : StepItemStatus.UNVISITED,
  };

  const createMonomer = async (): Promise<void> => {
    assert(
      [name, symbol, naturalAnalogue].every((field) => field != null),
      'Unexpected null input fields received for monomer creation.'
    );
    const nucleotideComponent = NaturalAnalogueToNucleotideComponent[naturalAnalogue];
    const input: APIMonomer = {
      name,
      symbol,
      naturalAnalogue: gqlToAPINaturalAnalogue(naturalAnalogue)!,
      smiles: smilesInput,
      visualSymbol: nucleotideComponent === NucleotideComponent.PHOSPHATE ? visualSymbol : undefined,
      color: nucleotideComponent !== NucleotideComponent.PHOSPHATE ? visualColor : undefined,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
    };

    try {
      LoadingScreen.show('Creating...');
      const result = await createMonomers([input]);
      notifications.success(`Created monomer ${result[0].symbol}`);
      navigateToURL('/monomers');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    } finally {
      LoadingScreen.hide();
    }
  };

  const buildPaneButtons = (
    <div>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        text="Next: Define"
        size={ButtonSize.MEDIUM}
        disabled={!buildPhaseCompleted}
        onClick={() => {
          setCurrentStepKey(MonomerManualCreationStepKey.DEFINE);
        }}
        dataTestAttrs={{
          component: 'MonomerManualCreationPage',
          element: 'nextButton',
        }}
      />
    </div>
  );

  const definePaneButtons = (
    <div className={css(styles.definePaneButtons)}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        text="Back"
        size={ButtonSize.MEDIUM}
        onClick={() => {
          setCurrentStepKey(MonomerManualCreationStepKey.BUILD);
        }}
      />
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        text="Add new monomer"
        size={ButtonSize.MEDIUM}
        disabled={!definePhaseCompleted || editingMolecularWeight || hasCustomWeightError}
        onClick={createMonomer}
        dataTestAttrs={{
          component: 'MonomerManualCreationPage',
          element: 'addButton',
        }}
      />
    </div>
  );

  const rightButtonGroup =
    currentStepKey === MonomerManualCreationStepKey.BUILD ? buildPaneButtons : definePaneButtons;

  const navSteps = [
    {
      key: MonomerManualCreationStepKey.BUILD,
      title: 'Build',
    },
    {
      key: MonomerManualCreationStepKey.DEFINE,
      title: 'Define',
    },
  ];

  return (
    <div
      className={css(styles.pageContainer)}
      {...getTestProps({component: 'MonomerManualCreationPage', element: 'root'})}
    >
      <MonomerConfigurationPageContainer methodTitle="Create new monomer" />
      <div className={css(styles.stepNav)}>
        <StepNav<MonomerManualCreationStepKey>
          steps={navSteps}
          statusByStep={statusByStep}
          currentStepKey={currentStepKey}
          onUpdateCurrentStep={() => {}}
        />
      </div>
      <div className={css(styles.middleSectionContainer)}>
        <div className={css(styles.monomerPropertiesPane)}>
          {currentStepKey === MonomerManualCreationStepKey.BUILD ? (
            <MonomerManualCreationBuildPane
              smilesInput={smilesInput}
              onChangeSmilesInput={onChangeSmilesInput}
              canonicalSmiles={canonicalSmiles}
              attachmentPointsInput={attachmentPointsInput}
              onChangeAttachmentPointsInput={onChangeAttachmentPointsInput}
              calculatedAttachmentPoints={calculatedAttachmentPoints}
              validationErrors={validationErrors}
            />
          ) : (
            <MonomerManualCreationDefinePane
              name={name}
              onChangeName={onChangeName}
              symbol={symbol}
              onChangeSymbol={onChangeSymbol}
              visualSymbol={visualSymbol}
              onChangeVisualSymbol={setVisualSymbol}
              visualColor={visualColor}
              onChangeVisualColor={setVisualColor}
              naturalAnalogue={naturalAnalogue}
              onChangeNaturalAnalogue={onChangeNaturalAnalogue}
              validationErrors={validationErrors}
            />
          )}
        </div>
        <MonomerStructureAndWeightConfigurationPane
          smilesInput={smilesInput}
          customMolecularWeight={customMolecularWeight}
          calculatedMolecularWeight={calculatedMolecularWeight}
          onChangeCustomMolecularWeight={onChangeCustomMolecularWeight}
          validationErrors={validationErrors}
          editingMolecularWeight={editingMolecularWeight}
          onChangeEditingMolecularWeight={setEditingMolecularWeight}
          structureImage={structureImage}
          hasCustomWeightError={hasCustomWeightError}
          onChangeHasCustomWeightError={setHasCustomWeightError}
        />
      </div>
      <div className={css(styles.submitBar)}>
        <div className={css(styles.buttonsContainer)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={() => {
              navigateToURL('/monomers');
            }}
            size={ButtonSize.MEDIUM}
            text="Cancel"
          />
          {rightButtonGroup}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'scroll',
  },
  stepNav: {
    paddingLeft: 40,
    paddingBottom: 20,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    flex: '0 0 auto',
  },
  middleSectionContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  },
  monomerPropertiesPane: {
    width: '50%',
    borderRight: `1px solid ${GRAY_LIGHT}`,
  },
  submitBar: {
    flex: '0 0 auto',
    boxSizing: 'border-box',
    padding: 16,
    gap: 10,
    boxShadow: '0px -2px 4px rgba(92, 124, 131, 0.1)',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  definePaneButtons: {
    display: 'flex',
    gap: 12,
  },
});
